/* ============================================================
   FREE GIFT AUTOMATION
   Runs up to two independent promotions in the cart:

   - "always"    — exactly one free gift line whenever the cart has at
                   least one other (non-gift) item. Removed once every
                   other item is gone.
   - "threshold" — exactly one free gift line once the cart subtotal
                   (excluding gifts) reaches the configured minimum
                   amount. Removed if the subtotal drops back below it.
                   Shows an animated popup (snippets/free-gift-modal.liquid)
                   the moment it's unlocked mid-session.

   Either promotion can be enabled independently, or both at once, each
   with its own gift product. Driven by the theme settings
   `free_gift_always_enabled` / `free_gift_enabled`. Config is injected
   by snippets/free-gift.liquid as window.__freeGift = { always, threshold }.
   ============================================================ */
(function () {
  'use strict';

  var RAW = window.__freeGift || {};

  function buildCfg(raw, kind) {
    if (!raw || !raw.enabled || !raw.variantId) return null;
    var cfg = {
      kind: kind,
      variantId: parseInt(raw.variantId, 10),
      handle: String(raw.handle || '').toLowerCase(),
      title: raw.title || '',
      available: raw.available !== false
    };
    if (kind === 'threshold') {
      cfg.minAmountCents = parseInt(raw.minAmountCents, 10) || 0;
      cfg.showModal = raw.showModal !== false;
      if (!(cfg.minAmountCents > 0)) return null;
    }
    return cfg;
  }

  var ALWAYS_CFG = buildCfg(RAW.always, 'always');
  var THRESHOLD_CFG = buildCfg(RAW.threshold, 'threshold');
  if (!ALWAYS_CFG && !THRESHOLD_CFG) return;

  // Root path (supports localized /en-ca/ style stores)
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  ROOT = ROOT.replace(/\/$/, '');
  function url(path) { return ROOT + path; }

  var busy = false;         // true while WE mutate the cart (guards recursion)
  var timer = null;

  // null = not yet determined (page load); true/false once a reconcile completes.
  var knownGiftPresent = { always: null, threshold: null };
  var giftState = {
    always: { present: false, title: '' },
    threshold: { present: false, title: '' }
  };

  // ── Identify a gift line item belonging to a given promotion ──
  function isGiftOf(item, cfg) {
    if (!item || !cfg) return false;
    if (item.properties && item.properties._free_gift === cfg.kind) return true;
    if (parseInt(item.variant_id, 10) === cfg.variantId) return true;
    return !!(item.handle && String(item.handle).toLowerCase() === cfg.handle);
  }

  function isAnyGift(item) {
    return isGiftOf(item, ALWAYS_CFG) || isGiftOf(item, THRESHOLD_CFG);
  }

  // ── Cart fetch ──
  function getCart() {
    return fetch(url('/cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function post(path, body) {
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  // ── Sections that must be refreshed after a gift change ──
  // Mirrors Dawn's own cart.js selectors so counts + cart page stay in sync.
  function getTargets() {
    var t = [];
    var items = document.getElementById('main-cart-items');
    if (items) t.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    var footer = document.getElementById('main-cart-footer');
    if (footer) t.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    if (document.getElementById('cart-icon-bubble')) {
      t.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    var live = document.getElementById('cart-live-region-text');
    if (live) t.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    return t;
  }

  function sectionInner(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector);
      return el ? el.innerHTML : null;
    } catch (e) { return null; }
  }

  function applySections(state, targets) {
    if (!state || !state.sections) return;
    targets.forEach(function (t) {
      var html = state.sections[t.section];
      if (!html) return;
      var host = document.getElementById(t.id);
      if (!host) return;
      var target = host.querySelector(t.selector) || host;
      var inner = sectionInner(html, t.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  // ── Toggle the "free gift included" confirmation inside the cart notification ──
  function renderGiftLine() {
    var line = document.querySelector('.cart-notification__gift');
    if (!line) return;
    var names = [];
    if (giftState.always.present && giftState.always.title) names.push(giftState.always.title);
    if (giftState.threshold.present && giftState.threshold.title) names.push(giftState.threshold.title);

    var nameEl = line.querySelector('[data-free-gift-name]');
    if (nameEl) {
      nameEl.textContent = names.length
        ? names.join(' + ') + ' added to your order'
        : 'A free gift has been added to your order';
    }
    line.hidden = names.length === 0;
  }

  // ── Free gift popup (snippets/free-gift-modal.liquid) — threshold gift only ──
  var modalOpener = null;
  var modalCloseTimer = null;

  function bindModalControls() {
    var backdrop = document.getElementById('fgm-backdrop');
    if (!backdrop || backdrop.__fgmBound) return;
    backdrop.__fgmBound = true;
    var closeBtn = document.getElementById('fgm-close');
    var continueBtn = document.getElementById('fgm-continue');
    if (closeBtn) closeBtn.addEventListener('click', closeGiftModal);
    if (continueBtn) continueBtn.addEventListener('click', closeGiftModal);
    backdrop.addEventListener('click', function (evt) {
      if (evt.target === backdrop) closeGiftModal();
    });
  }

  function onModalKeydown(evt) {
    if (evt.key === 'Escape' || evt.code === 'Escape') closeGiftModal();
  }

  function openGiftModal() {
    var backdrop = document.getElementById('fgm-backdrop');
    if (!backdrop) return;
    if (modalCloseTimer) { clearTimeout(modalCloseTimer); modalCloseTimer = null; }
    modalOpener = document.activeElement;
    document.body.classList.add('fgm-locked');
    backdrop.classList.remove('is-exiting');
    backdrop.style.display = 'flex';
    backdrop.setAttribute('aria-hidden', 'false');
    // Force layout so the entrance transition actually plays.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { backdrop.classList.add('is-visible'); });
    });
    document.addEventListener('keydown', onModalKeydown);
  }

  function closeGiftModal() {
    var backdrop = document.getElementById('fgm-backdrop');
    if (!backdrop || !backdrop.classList.contains('is-visible')) return;
    backdrop.classList.remove('is-visible');
    backdrop.classList.add('is-exiting');
    backdrop.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fgm-locked');
    document.removeEventListener('keydown', onModalKeydown);
    modalCloseTimer = setTimeout(function () {
      backdrop.classList.remove('is-exiting');
      backdrop.style.display = 'none';
      if (modalOpener && typeof modalOpener.focus === 'function') modalOpener.focus();
    }, 320);
  }

  // ── Core reconcile — evaluates both promotions against one cart snapshot ──
  function reconcile() {
    timer = null;
    if (busy) return;

    getCart().then(function (cart) {
      var items = (cart && cart.items) || [];

      var otherQty = 0;
      var otherSubtotal = 0;
      items.forEach(function (it) {
        if (!isAnyGift(it)) {
          otherQty += it.quantity;
          otherSubtotal += it.line_price || 0;
        }
      });

      var plans = [];
      [ALWAYS_CFG, THRESHOLD_CFG].forEach(function (cfg) {
        if (!cfg) return;
        var giftLines = items.filter(function (it) { return isGiftOf(it, cfg); });
        var giftQty = giftLines.reduce(function (sum, l) { return sum + l.quantity; }, 0);
        var desired = cfg.kind === 'always'
          ? (otherQty > 0 ? 1 : 0)
          : (otherSubtotal >= cfg.minAmountCents ? 1 : 0);
        plans.push({ cfg: cfg, giftLines: giftLines, giftQty: giftQty, desired: desired });
      });

      var wasKnownThreshold = knownGiftPresent.threshold;

      var needsChange = plans.some(function (p) {
        return !(p.giftLines.length <= 1 && p.giftQty === p.desired);
      });

      if (!needsChange) {
        plans.forEach(function (p) {
          giftState[p.cfg.kind].present = p.giftQty > 0;
          giftState[p.cfg.kind].title = p.cfg.title;
          knownGiftPresent[p.cfg.kind] = p.giftQty > 0;
        });
        renderGiftLine();
        return;
      }

      var targets = getTargets();
      var sectionIds = targets.map(function (t) { return t.section; });

      var updates = {};
      var addItems = [];
      var addingKinds = [];

      plans.forEach(function (p) {
        var balanced = p.giftLines.length <= 1 && p.giftQty === p.desired;
        if (balanced) return;

        if (p.desired > 0 && p.giftLines.length === 0) {
          if (p.cfg.available === false) return; // can't add an out-of-stock gift
          addItems.push({ id: p.cfg.variantId, quantity: 1, properties: { _free_gift: p.cfg.kind } });
          addingKinds.push(p.cfg.kind);
        } else {
          // Fix quantity / collapse duplicates / remove entirely.
          p.giftLines.forEach(function (l, i) { updates[l.key] = i === 0 ? p.desired : 0; });
        }
      });

      busy = true;
      var chain = Promise.resolve(null);

      if (Object.keys(updates).length) {
        chain = chain.then(function () {
          return post('/cart/update.js', { updates: updates, sections: sectionIds, sections_url: window.location.pathname });
        });
      }
      if (addItems.length) {
        chain = chain.then(function () {
          return post('/cart/add.js', { items: addItems, sections: sectionIds, sections_url: window.location.pathname });
        });
      }

      chain.then(function (state) {
        if (state) applySections(state, targets);

        plans.forEach(function (p) {
          var attemptedAdd = p.desired > 0 && p.giftLines.length === 0;
          var skippedUnavailable = attemptedAdd && p.cfg.available === false;
          var present = p.desired > 0 && !skippedUnavailable;
          giftState[p.cfg.kind].present = present;
          giftState[p.cfg.kind].title = p.cfg.title;
          knownGiftPresent[p.cfg.kind] = present;
        });
        renderGiftLine();

        if (
          THRESHOLD_CFG &&
          THRESHOLD_CFG.showModal &&
          addingKinds.indexOf('threshold') !== -1 &&
          wasKnownThreshold === false
        ) {
          openGiftModal();
        }
      }).catch(function () {}).then(function () {
        busy = false;
      });
    }).catch(function () { busy = false; });
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, 300);
  }

  // ── Intercept every cart mutation so we reconcile no matter the source ──
  // (product form, quick add, cart page qty change, drawer, 3rd-party scripts).
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var origFetch = window.fetch;
  window.fetch = function (input) {
    var p = origFetch.apply(this, arguments);
    try {
      var u = typeof input === 'string' ? input : (input && input.url) || '';
      if (MUTATION.test(u)) {
        p.then(function () { if (!busy) schedule(); }, function () {});
      }
    } catch (e) { /* never let our hook break a cart request */ }
    return p;
  };

  // Also cover the legacy XHR path some apps still use.
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, u) {
      if (u && MUTATION.test(u)) {
        this.addEventListener('load', function () { if (!busy) schedule(); });
      }
      return origOpen.apply(this, arguments);
    };
  } catch (e) { /* ignore */ }

  function init() {
    bindModalControls();
    // Initial pass — handles: setting just enabled, direct cart-page landing,
    // or a returning cart that is missing / has a stale gift. Never pops the
    // modal on its own (wasKnownThreshold is still null on this first call).
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-check when restored from the back/forward cache.
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });
})();
